import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/useMobile";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { DEMO_OPEN_ID } from "@shared/const";
import {
  LayoutDashboard,
  LogOut,
  MessageSquareText,
  ArrowLeftRight,
  Upload,
  FileText,
  PieChart,
  Target,
  Download,
  Sun,
  Moon,
  Globe,
  X,
  MoreHorizontal,
  UserCog,
  Landmark,
  Building2,
  FolderOpen,
  UsersRound,
  ReceiptText,
  Link2,
  BadgeDollarSign,
  BookOpen,
  ChartNoAxesCombined,
  ScrollText,
  ChefHat,
} from "lucide-react";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();
  const userId = user?.id;
  const isMobileTop = useIsMobile();
  const accountTypeStatus = trpc.accountType.status.useQuery(undefined, { enabled: !loading && !!userId });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  useEffect(() => {
    if (!loading && !userId) {
      window.location.replace("/signin");
    }
  }, [loading, userId]);

  useEffect(() => {
    if (!loading || userId) return;
    const fallback = window.setTimeout(() => window.location.replace("/signin"), 4500);
    return () => window.clearTimeout(fallback);
  }, [loading, userId]);

  useEffect(() => {
    if (!loading && userId && accountTypeStatus.data?.accountTypeCompleted === false) {
      window.location.replace("/account-type");
    }
  }, [accountTypeStatus.data?.accountTypeCompleted, loading, userId]);

  if (loading || (user && accountTypeStatus.isLoading)) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return <div className="min-h-screen min-h-[100dvh] bg-background" aria-busy="true" />;
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": isMobileTop ? "0px" : `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { state } = useSidebar();
  const { theme, toggleTheme, switchable } = useTheme();
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const featureStatus = trpc.feature.businessStatus.useQuery();
  const workspaceStatus = trpc.auth.workspace.useQuery(undefined, { enabled: !!user });
  const utils = trpc.useUtils();
  const switchWorkspace = trpc.auth.switchWorkspace.useMutation({
    onSuccess: async () => {
      await utils.invalidate();
    },
  });

  const isDemoUser = user?.openId === DEMO_OPEN_ID;
  const businessEnabled = featureStatus.data?.enabled === true;
  const isBusinessWorkspace = workspaceStatus.data?.activeWorkspace === "business" && workspaceStatus.data.businessAvailable;
  const canSwitchWorkspace = workspaceStatus.data?.businessAvailable === true;
  const managedBusinessEntities = trpc.entity.list.useQuery(undefined, { enabled: isBusinessWorkspace === true });
  const activeBusinessEntity = managedBusinessEntities.data?.entities.find((entity) => entity.id === managedBusinessEntities.data?.activeEntityId) ?? null;

  const personalMenuItems = [
    { icon: LayoutDashboard, label: t("nav.dashboard"), path: "/dashboard" },
    { icon: FileText, label: t("nav.incomeStatement"), path: "/income-statement" },
    { icon: PieChart, label: t("nav.analytics"), path: "/analytics" },
    { icon: ArrowLeftRight, label: t("nav.transactions"), path: "/transactions" },
    { icon: Target, label: t("nav.budgets"), path: "/budgets" },
    { icon: Upload, label: t("nav.smsParser"), path: "/sms" },
    { icon: Download, label: t("nav.export"), path: "/export" },
  ];
  const businessMenuItems = [
    { icon: LayoutDashboard, label: t("nav.dashboard"), path: "/dashboard" },
    { icon: ChartNoAxesCombined, label: t("businessStatements.title"), path: "/business-statements" },
    { icon: PieChart, label: t("nav.analytics"), path: "/business-analytics" },
    { icon: ChefHat, label: t("services.title"), path: "/services" },
    { icon: Upload, label: t("nav.upload"), path: "/business-upload" },
    { icon: ReceiptText, label: t("invoice.title"), path: "/invoices" },
    { icon: UsersRound, label: t("nav.contacts"), path: "/counterparties" },
    { icon: FolderOpen, label: t("documents.title"), path: "/documents" },
  ];
  const alwaysMenuItems = [
    { icon: UserCog, label: t("nav.accountSettings"), path: "/account" },
  ];
  const primaryMenuItems = isBusinessWorkspace ? businessMenuItems : personalMenuItems;
  const workspaceUtilityItems = alwaysMenuItems;
  const menuItems = [...primaryMenuItems, ...workspaceUtilityItems];

  // At most four direct links plus More on mobile; all remaining active-entity links stay discoverable there.
  const bottomNavItems = primaryMenuItems.slice(0, 4);
  const moreNavItems = [...primaryMenuItems.slice(4), ...workspaceUtilityItems];

  const activeMenuItem = menuItems.find((item) => item.path === location);
  const isMoreActive = moreNavItems.some((item) => item.path === location);
  const selectWorkspace = async (workspace: "individual" | "business") => {
    if (workspace === "business" && !workspaceStatus.data?.businessAvailable) {
      setLocation("/account-type");
      return;
    }
    if (workspace === workspaceStatus.data?.activeWorkspace) return;
    await switchWorkspace.mutateAsync({ workspace });
    setLocation("/dashboard");
  };

  useEffect(() => {
    const businessRoutes = ["/documents", "/counterparties", "/invoices", "/reconciliation", "/contracts", "/ledger", "/statement-import", "/business-statements", "/business-upload", "/business-analytics", "/services"];
    const personalRoutes = ["/income-statement", "/analytics", "/transactions", "/budgets", "/export", "/accounts"];
    if (workspaceStatus.data?.activeWorkspace === "business" && !workspaceStatus.data.businessAvailable) {
      if (location !== "/dashboard") setLocation("/dashboard");
      return;
    }
    if (!isBusinessWorkspace && businessRoutes.includes(location)) setLocation("/dashboard");
    if (isBusinessWorkspace && personalRoutes.includes(location)) setLocation("/dashboard");
  }, [isBusinessWorkspace, location, setLocation, workspaceStatus.data]);

  const handleExitDemo = async () => {
    await logout();
    window.location.href = "/";
  };

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarRect = sidebarRef.current?.getBoundingClientRect();
      if (!sidebarRect) return;
      let newWidth: number;
      if (isRTL) {
        newWidth = sidebarRect.right - e.clientX;
      } else {
        newWidth = e.clientX - sidebarRect.left;
      }
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth, isRTL]);

  // Mobile/Tablet layout with bottom navigation
  if (isMobile) {
    return (
      <div className="flex flex-col min-h-screen min-h-[100dvh] w-full overflow-x-hidden">
        {/* Mobile top header */}
        <div className="flex items-center border-b justify-between bg-background/95 px-3 pb-3 pt-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40 safe-area-top">
          <div className="flex items-center gap-2">
            <img
              src="https://d2xsxph8kpxj0f.cloudfront.net/98466279/N8L9TKRXjtLMpzosC4Ao5k/mizano-icon-exact-192_e336f133.png"
              alt="Mizano"
              className="h-7 w-7 rounded-lg"
            />
            <span className="text-sm font-semibold tracking-tight text-foreground truncate">
              {activeMenuItem?.label ?? t("app.title")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {canSwitchWorkspace && (
              <button onClick={() => selectWorkspace(isBusinessWorkspace ? "individual" : "business")} className="inline-flex min-h-11 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring">
                <Building2 className="h-4 w-4 shrink-0" />
                <span>{t("workspace.switch")}</span>
              </button>
            )}
            {isDemoUser && (
              <button
                onClick={handleExitDemo}
                className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md px-2.5 text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                <span>{t("menu.exitDemo")}</span>
              </button>
            )}
              <DropdownMenu>
              <DropdownMenuTrigger aria-label={user?.name ?? t("app.title")} className="flex min-h-11 min-w-11 items-center justify-center rounded-full bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="text-xs font-medium">{user?.name?.charAt(0).toUpperCase()}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={toggleLanguage}
                  className="cursor-pointer"
                >
                  <Globe className="me-2 h-4 w-4" />
                  <span>{t("menu.language")}</span>
                </DropdownMenuItem>
                {switchable && (
                  <DropdownMenuItem
                    onClick={toggleTheme}
                    className="cursor-pointer"
                  >
                    {theme === "dark" ? (
                      <Sun className="mr-2 h-4 w-4" />
                    ) : (
                      <Moon className="mr-2 h-4 w-4" />
                    )}
                    <span>{theme === "dark" ? t("menu.lightMode") : t("menu.darkMode")}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t("menu.signOut")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 w-full overflow-x-hidden p-3 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-3">{children}</main>

        {/* Bottom navigation bar (Instagram-style) */}
        <nav className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:backdrop-blur safe-area-bottom">
          <div className="flex items-center justify-around h-16 px-2">
            {bottomNavItems.map((item) => {
              const isActive = location === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => setLocation(item.path)}
                  className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  <item.icon className={`h-5 w-5 ${isActive ? "text-primary" : ""}`} />
                  <span className="text-[11px] font-medium leading-tight truncate max-w-[60px]">
                    {item.label}
                  </span>
                </button>
              );
            })}
            {/* More button with dropdown for remaining items */}
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={t("nav.more") || "More"}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors ${
                  isMoreActive ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <MoreHorizontal className={`h-5 w-5 ${isMoreActive ? "text-primary" : ""}`} />
                <span className="text-[11px] font-medium leading-tight">
                  {t("nav.more") || "More"}
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-48 mb-2">
                {moreNavItems.map((item) => {
                  const isActive = location === item.path;
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => setLocation(item.path)}
                      className={`cursor-pointer ${isActive ? "text-primary font-medium" : ""}`}
                    >
                      <item.icon className="mr-2 h-4 w-4" />
                      <span>{item.label}</span>
                    </DropdownMenuItem>
                  );
                })}
                {canSwitchWorkspace && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => selectWorkspace(isBusinessWorkspace ? "individual" : "business")} className="cursor-pointer">
                      <Building2 className="mr-2 h-4 w-4" />
                      <span>{t("workspace.switch")}</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>
      </div>
    );
  }

  // Desktop layout with sidebar (unchanged)
  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          side={isRTL ? "right" : "left"}
          disableTransition={isResizing}
        >
          <SidebarHeader className="h-16 justify-center">
            <div className="flex items-center gap-2 px-2 transition-all w-full">
              <img
                src="https://d2xsxph8kpxj0f.cloudfront.net/98466279/N8L9TKRXjtLMpzosC4Ao5k/mizano-icon-exact-192_e336f133.png"
                alt="Mizano"
                className="h-8 w-8 rounded-lg shrink-0"
              />
              {!isCollapsed ? (
                <div className="min-w-0">
                  <p className="truncate font-semibold tracking-tight text-foreground">{t("app.title")}</p>
                  {activeBusinessEntity ? <p className="truncate text-xs text-muted-foreground" title={activeBusinessEntity.name}>{language === "ar" ? activeBusinessEntity.nameAr || activeBusinessEntity.name : activeBusinessEntity.name}</p> : null}
                </div>
              ) : null}
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-0">
            <SidebarMenu className="px-2 py-1">
              {primaryMenuItems.map((item) => {
                const isActive = location === item.path;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => setLocation(item.path)}
                      aria-label={item.label}
                      title={isCollapsed ? item.label : undefined}
                      className={`h-10 transition-all font-normal`}
                    >
                      <item.icon
                        className={`h-4 w-4 ${isActive ? "text-primary" : ""}`}
                      />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              <div className="mx-2 my-2 border-t border-border/70" />
              {alwaysMenuItems.map((item) => {
                const isActive = location === item.path;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton isActive={isActive} onClick={() => setLocation(item.path)} aria-label={item.label} title={item.label} className="h-10 transition-all font-normal">
                      <item.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarContent>

          <SidebarFooter className="p-3">
            {isDemoUser && (
              <button
                onClick={handleExitDemo}
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 mb-2 w-full text-sm font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2"
              >
                <X className="h-4 w-4 shrink-0" />
                <span className="group-data-[collapsible=icon]:hidden">{t("menu.exitDemo")}</span>
              </button>
            )}
            {/* Privacy & Terms */}
            <div className="flex items-center gap-2 px-2 mb-2 text-[11px] text-muted-foreground group-data-[collapsible=icon]:hidden">
              <a href="/privacy" className="hover:text-foreground transition-colors">
                {language === "ar" ? "الخصوصية" : "Privacy"}
              </a>
              <span>•</span>
              <a href="/terms" className="hover:text-foreground transition-colors">
                {language === "ar" ? "الشروط" : "Terms"}
              </a>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger aria-label={user?.name ?? t("app.title")} className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Avatar className="h-9 w-9 border shrink-0">
                  <AvatarFallback className="text-xs font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                  <p className="text-sm font-medium truncate leading-none text-foreground">
                    {user?.name || "-"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate mt-1.5">
                    {user?.email || "-"}
                  </p>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={toggleLanguage}
                  className="cursor-pointer"
                >
                  <Globe className="mr-2 h-4 w-4" />
                  <span>{t("menu.language")}</span>
                </DropdownMenuItem>
                {switchable && (
                  <DropdownMenuItem
                    onClick={toggleTheme}
                    className="cursor-pointer"
                  >
                    {theme === "dark" ? (
                      <Sun className="mr-2 h-4 w-4" />
                    ) : (
                      <Moon className="mr-2 h-4 w-4" />
                    )}
                    <span>{theme === "dark" ? t("menu.lightMode") : t("menu.darkMode")}</span>
                  </DropdownMenuItem>
                )}
                {canSwitchWorkspace && (
                  <DropdownMenuItem onClick={() => selectWorkspace(isBusinessWorkspace ? "individual" : "business")} className="cursor-pointer">
                    <Building2 className="mr-2 h-4 w-4" />
                    <span>{t("workspace.switch")}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t("menu.signOut")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 ${isRTL ? "start-0" : "end-0"} h-full w-1 cursor-col-resize transition-colors hover:bg-primary/20 ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        <main className="flex-1 p-3 sm:p-4 md:p-6">{children}</main>
      </SidebarInset>
    </>
  );
}
